import * as React from "react";
import { request } from "../../../services/cpln";
import { MARKETING_URL } from "../../../envVariables";
import { Loader } from "../../../components/layout/loader";
import { sentryLog } from "../../../tools/sentry";
import { BillingAccountSummary } from "../../../schema/types/account";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { setOrg as setContextOrg } from "../../../store/contextSlice";
import { setProvider } from "../../../store/userSlice";

interface Props {
  awsAccountId: string;
  providerId: string;
  billingAccountId: string;
}
export const Login: React.FC<Props> = ({ awsAccountId, providerId, billingAccountId }) => {
  const dispatch = useAppDispatch();
  const theme = useAppSelector((state) => state.theme.theme);
  const userEmail = useAppSelector((state) => state.user.email);

  const [inited, setInited] = React.useState(false);
  React.useEffect(() => {
    init();
  }, []);

  const [accountSummary, setAccountSummary] = React.useState<BillingAccountSummary | null>(null);
  const [accountHasOrg, setAccountHasOrg] = React.useState<boolean>(false);
  const [org, setOrg] = React.useState<string | null>(null);

  const [isProcessing, setIsProcessing] = React.useState<boolean>(false);

  async function init() {
    let accountSummary: BillingAccountSummary | null = null;
    try {
      const { data } = await request({ service: "billing-ng", url: `/account/${billingAccountId}` });
      accountSummary = data;
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      const logData: Record<string, string> = {
        "data.accountId": billingAccountId,
      };
      if (errorMessage) {
        logData["data.errorMessage"] = errorMessage;
      }
      if (e.response?.status) {
        logData["data.status"] = e.response?.status;
      }
      sentryLog(`AWS Marketplace account is not accessible`, logData);
    }
    setAccountSummary(accountSummary || null);

    let org = "";
    let accountOrgs: string[] = [];

    if (accountSummary) {
      const { data: accountOrgsRes } = await request({
        service: "billing-ng",
        url: `/account/${accountSummary.id}/org`,
      });
      accountOrgs = accountOrgsRes.items.map((i: any) => i.name);
      setAccountHasOrg(accountOrgs.length > 0);

      const { data: profileOrgsRes } = await request({ service: "api", url: `/org` });
      const profileOrgs: string[] = profileOrgsRes.items.map((i) => i.name);

      const localOrg = localStorage.getItem(`org`);
      if (localOrg && accountOrgs.includes(localOrg) && profileOrgs.includes(localOrg)) {
        org = localOrg;
        setOrg(localOrg);
      } else {
        for (const accountOrg of accountOrgs) {
          if (profileOrgs.includes(accountOrg)) {
            org = accountOrg;
            setOrg(accountOrg);
            break;
          }
        }
      }
    }

    if (!org) {
      sentryLog(`AWS Marketplace account has no org`, { "data.orgs": accountOrgs });
    }

    setInited(true);
  }

  React.useEffect(() => {
    if (!inited) {
      return;
    }

    if (accountSummary && !org) {
      setTimeout(() => {
        processAccount();
      }, 700);
      return;
    }

    if (accountSummary && org) {
      setTimeout(() => {
        processOrg();
      }, 700);
    }
  }, [inited]);

  async function processAccount() {
    dispatch(setProvider("controlplane"));
    window.location.href = `/home/account/${accountSummary?.id!}`;
  }

  async function processOrg() {
    setIsProcessing(true);
    dispatch(setContextOrg(org || ""));
    setIsProcessing(false);
    dispatch(setProvider("controlplane"));
  }

  if (!inited) {
    return (
      <div>
        <div>Processing... Please wait</div>
        <Loader />
      </div>
    );
  }

  return (
    <div style={{ minWidth: 500, maxWidth: 800 }}>
      <div className="w-full flex justify-center mb-8">
        <a href={`${MARKETING_URL}`} target={"_blank"} rel={`noopener`} style={{ width: 300, height: 110 }}>
          <img
            className="controlplane-logo"
            height={"auto"}
            src={`/resources/logos/controlPlaneLogo${theme === "dark" ? "White" : ""}.svg`}
            alt="Control Plane Logo"
          />
        </a>
      </div>
      {accountSummary ? (
        <>
          {org ? (
            <div className="flex justify-center">
              <div className="text-xl">
                Redirecting to org: <span className="font-semibold">{org}</span>
                {isProcessing ? <Loader /> : null}
              </div>
            </div>
          ) : accountHasOrg ? (
            <div className="flex justify-center">
              <div className="text-xl">
                {/* // is it even possible to see this stage?  */}
                This user profile is not part of the orgs of the billing account of this AWS Subcription
                {isProcessing ? <Loader /> : null}
              </div>
            </div>
          ) : (
            <div className="flex justify-center">
              <div className="text-xl">
                Connected Billing Account does not have any Org. You will be redirected to Billing Account Details.
                {isProcessing ? <Loader /> : null}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-col gap-4">
          <div>
            AWS Account is connected to Control Plane Billing Account with ID{" "}
            <span className="font-semibold">{billingAccountId}</span>
          </div>
          <div>That billing account is not accessible by this user.</div>
          <div>
            If you think this is a problem, please reach us at support@controlplane.com with the information shown
            below.
          </div>
          <div>
            <div className="mb-2">
              <div className="text-sm">User Email</div>
              <div className="font-semibold">{userEmail}</div>
            </div>
            <div className="mb-2">
              <div className="text-sm">AWS Account ID</div>
              <div className="font-semibold">{awsAccountId}</div>
            </div>
            <div className="mb-2">
              <div className="text-sm">Connected Billing Account ID</div>
              <div className="font-semibold">{billingAccountId}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
